import { Action } from "redux";
import { ThunkAction } from "redux-thunk";
import queryString from "query-string";
import { endpoints } from "../Constants/endpoints";
import { IAppState, ApiRequest } from "../../Common";
import { FeedbackCategory } from "../../Feedback";
import ResponseHandler from "../../App/ResponseHandler";
import { ResponseResult } from "../../Common/Models/ResponseResult";
import { AutocompleteItem } from "../Models/AutocompleteItem";
import { SearchContext } from "../Models/SearchContext";

interface IAutocompleteSearchAction extends Action {
  type: AutocompleteSearchActionType;
  payload: any;
}

// ARGUMENT TYPES

export interface IGetAutocompleteResultsArguments {
  searchQuery: string;
  maxNumToGet?: number;
  category?: FeedbackCategory;
  searchContext?: SearchContext;
}

// PAYLOAD TYPES

export interface IAutocompleteSearchLoadedPayload {
  loadedItems: AutocompleteItem[];
}

export interface ISetAutocompleteSearchErroredPayload {
  errorCode: number;
  errorDescription: string;
}

export interface IAutocompleteSearchUnloadPayload {}

export interface ISetAutocompleteSearchLoadingPayload {}

// ACTION TYPES

export interface IAutocompleteSearchLoadedAction extends IAutocompleteSearchAction {
  payload: IAutocompleteSearchLoadedPayload;
}

// Action for the FEEDBACK_SEARCH_SET_ERRORED action
export interface ISetAutocompleteSearchErroredAction extends IAutocompleteSearchAction {
  payload: ISetAutocompleteSearchErroredPayload;
}

export interface IAutocompleteSearchUnloadAction extends IAutocompleteSearchAction {
  payload: IAutocompleteSearchUnloadPayload;
}

export interface ISetAutocompleteSearchLoadingAction extends IAutocompleteSearchAction {
  payload: ISetAutocompleteSearchLoadingPayload;
}

// union of all action types. If you define a new action type, add it here por favor
export type AutocompleteSearchAction =
  | IAutocompleteSearchLoadedAction
  | ISetAutocompleteSearchErroredAction
  | IAutocompleteSearchUnloadAction
  | ISetAutocompleteSearchLoadingAction;

export enum AutocompleteSearchActionType {
  AUTOCOMPLETE_SEARCH_LOADED = "AUTOCOMPLETE_SEARCH_LOADED",
  AUTOCOMPLETE_SEARCH_UNLOAD = "AUTOCOMPLETE_SEARCH_UNLOAD",
  AUTOCOMPLETE_SEARCH_SET_ERRORED = "AUTOCOMPLETE_SEARCH_SET_ERRORED",
  AUTOCOMPLETE_SEARCH_SET_LOADING = "AUTOCOMPLETE_SEARCH_SET_LOADING"
}

const setError = (status: number, description: string) => {
  return {
    type: AutocompleteSearchActionType.AUTOCOMPLETE_SEARCH_SET_ERRORED,
    payload: {
      errorCode: status,
      errorDescription: description
    }
  };
};

const searchLoaded = (loadedItems: AutocompleteItem[]) => {
  return {
    type: AutocompleteSearchActionType.AUTOCOMPLETE_SEARCH_LOADED,
    payload: {
      loadedItems
    }
  };
};

export const setLoading = (): ISetAutocompleteSearchLoadingAction => ({
  type: AutocompleteSearchActionType.AUTOCOMPLETE_SEARCH_SET_LOADING,
  payload: {}
});

export const unloadSearch = (): IAutocompleteSearchUnloadAction => ({
  type: AutocompleteSearchActionType.AUTOCOMPLETE_SEARCH_UNLOAD,
  payload: {}
});

function assembleAutocompleteUrl(args: IGetAutocompleteResultsArguments): string {
  let url: string = endpoints.search.autocomplete();

  const queryArgs = {
    query: args.searchQuery,
    maxSuggestionsToGet: args.maxNumToGet,
    category: args.category ? args.category.toString().replace(" ", "") : args.category,
    context: args.searchContext ? args.searchContext.type : null,
    contextItemId: args.searchContext ? args.searchContext.itemId : null
  };

  const stringified = queryString.stringify(queryArgs);

  url += `?${stringified}`;

  return url;
}

// runs the autocomplete search on the server asynchronously and dispatches an AUTOCOMPLETE_SEARCH_LOADED action
export const getAutocompleteResult = (
  args: IGetAutocompleteResultsArguments
): ThunkAction<
  void,
  IAppState,
  {},
  IAutocompleteSearchLoadedAction | ISetAutocompleteSearchErroredAction
> => dispatch => {
  ApiRequest.fromPath(assembleAutocompleteUrl(args))
    .withAuthHeader()
    .get()
    .then((response: Response) => {
      ResponseHandler.handle(
        response,
        (result: ResponseResult) => {
          dispatch(searchLoaded(result.payload));
        },
        (result: ResponseResult) => {
          dispatch(setError(response.status, result.error.message));
        },
        () => {
          dispatch(setError(response.status, "An unknown error has occured"));
        }
      );
    });
};
